import { Component, computed, effect, signal, ChangeDetectionStrategy, NgZone } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Register } from '@app/common-codes/decorators/DependencyInjectionRegistreyDecorators';
import { TranslatePipe } from '@app/common-codes/components/directives/TranslatePipe';
import { TranslationService } from '@app/common-codes/services/TranslationService';
import { GenericValidationService } from '@app/common-codes/services/GenericValidationService';
import { Visit } from '@app/common-codes/models/matwins-tenant/Visit.model';
import { Visitor } from '@app/common-codes/models/matwins-tenant/Visitor.model';
import { NavigationService } from '@app/common-codes/services/navigation.service';
import { CurrentTenantService } from '@app/common-codes/services/CurrentTenantService';
import { AlertModalService, ToastType } from '@app/common-codes/services/AlertModalService';
import { MultiSelectComponent } from '@app/common-codes/components/nested-components/multi-select/multi-select.component';
import { BubbleSmileyComponent } from '@app/common-codes/components/nested-components/bubble-smiley/bubble-smiley.component';
import { GenetricDetailComponent } from '@app/common-codes/components/edit-components/generic-detail.component';
import { MatwinsSkins } from '@app/common-codes/skins/global.skin';
import { IStaticStylesConfig } from '@app/common-codes/skins/_primitives/IStaticStylesConfig';
import { IEntityMetaData } from '@app/common-codes/_models/IEntityMetaData';

/**
 * Check-out visiteur en page unique pour le flux QR Code.
 * Le visiteur se recherche par nom parmi les visites actives du POS
 * (editingEntity = visite sélectionnée). Si le POS a dS_Checkout_Feedbacks,
 * un feedback (smileys + commentaire) est demandé avant la sortie,
 * sinon le checkout est immédiat.
 */
@Register(Visit)
@Component({
    selector: 'app-qrcode-visitor-checkout',
    templateUrl: './qrcode-visitor-checkout.component.html',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [FormsModule, TranslatePipe, MultiSelectComponent, BubbleSmileyComponent]
})
export class QrcodeVisitorCheckoutComponent extends GenetricDetailComponent<Visit> {

    override Skin: IStaticStylesConfig = MatwinsSkins.QrCode1;
    override ComponentEntityClass = Visit;
    override entityMetaData: IEntityMetaData = new Visit().GetType();
    override Editable = false;

    // Page QR Code publique : pas de permissions requises (comme le checkin)
    public override get RequiredPermissions(): string[] {
        return [];
    }
    public override get HasRequiredPermissions(): boolean {
        return true;
    }

    /** Fenêtre de recherche des visites actives : les dernières 24h seulement. */
    private readonly ACTIVE_VISITS_WINDOW_HOURS = 24;

    activeVisits = signal<Visit[]>([]);
    visitsLoaded = signal(false);
    selectedVisitorId = signal<string[]>([]);
    feedbackScore = signal<number | undefined>(undefined);
    feedbackComment = signal('');

    pos = computed(() => this.tenantService.currentPointOfService());

    showFeedbackForm = computed(() => {
        const pos = this.pos();
        return !!pos?.dS_Checkout_Feedbacks && !!this.editingEntity();
    });

    activeVisitorsLookup = computed(() => {
        // Toutes les visites actives sont passées au multi-select qui filtre localement
        return this.activeVisits().map(v => ({
            id: v.visitor?.id?.toString() ?? '',
            key: v.visitor?.id?.toString() ?? '',
            value: v.visitor?.fullname ?? '',
            sortOrder: '',
            visible: true,
            additionalData: { visitId: v.id, startDate: v.startDate },
            children: undefined,
            selectedValue: undefined
        })).filter(item => !!item.id);
    });

    profileNotFound = computed(() => {
        return this.visitsLoaded()
            && this.selectedVisitorId().length === 0
            && this.activeVisitorsLookup().length === 0;
    });

    profileFound = computed(() => {
        return !!this.editingEntity() && !this.showFeedbackForm();
    });

    constructor(
        route: ActivatedRoute,
        router: Router,
        translationService: TranslationService,
        validationService: GenericValidationService,
        ngZone: NgZone,
        private navService: NavigationService,
        private tenantService: CurrentTenantService,
        alertModalService: AlertModalService
    ) {
        super(route, router, translationService, validationService, ngZone);
        this.alertModalService = alertModalService;

        // Le POS peut arriver après l'init (deep-link direct sur la page)
        effect(() => {
            const pos = this.pos();
            if (pos) {
                this.loadAllActiveVisits();
            }
        });
    }

    override getEmptyEntity(): Visit {
        const visit = new Visit();
        visit.visitor = new Visitor();
        visit.startDate = new Date();
        visit.status = 1; // In
        visit.fK_PointOfService = this.tenantService?.currentPointOfServiceId() || 0;
        return visit;
    }

    override parseToEntityInstance(): void {
        if (this.EntityInstance && this.editingEntity()) {
            Object.assign(this.EntityInstance, this.editingEntity());
        }
    }

    async loadAllActiveVisits(): Promise<void> {
        const pos = this.tenantService.currentPointOfService();
        if (!pos) return;

        this.loaderService.show();
        try {
            const visits = await this.dataService.execute<Visit[]>('GET', 'getActiveVisits', {
                fkPos: pos.id,
                sinceHours: this.ACTIVE_VISITS_WINDOW_HOURS
            });
            this.activeVisits.set(visits ?? []);
            this.visitsLoaded.set(true);
        } catch (err) {
            console.error('Erreur chargement visites actives:', err);
            this.alertModalService?.showToast(
                this.translationService.instant('vues.qr-code.checkout-form.error-load'),
                ToastType.Error,
                3000
            );
        } finally {
            this.loaderService.hide();
        }
    }

    async onVisitorSelectChange(ids: string[]): Promise<void> {
        this.selectedVisitorId.set(ids);
        this.feedbackScore.set(undefined);
        this.feedbackComment.set('');

        const visitorId = ids[0];
        if (!visitorId) {
            this.editingEntity.set(null);
            return;
        }

        const visit = this.activeVisits().find(v => v.visitor?.id?.toString() === visitorId);
        this.editingEntity.set(visit ?? null);

        if (visit && !this.pos()?.dS_Checkout_Feedbacks) {
            await this.autoCheckout(visit);
        }
    }

    async autoCheckout(visit: Visit): Promise<void> {
        const pos = this.tenantService.currentPointOfService();
        if (!pos) return;
        this.loaderService.show();

        try {
            await this.dataService.execute<Visit>('POST', 'checkoutVisit', {
                visitId: visit.id,
                posId: pos.id
            });
            this.showSuccessAndGoHome(1500);
        } catch (err) {
            console.error('Erreur checkout:', err);
            this.alertModalService?.showToast(
                this.translationService.instant('vues.qr-code.checkout-form.error-checkout'),
                ToastType.Error,
                3000
            );
        } finally {
            this.loaderService.hide();
        }
    }

    setFeedbackScore(score: number): void {
        this.feedbackScore.set(score);
    }

    async submitCheckout(): Promise<void> {
        const pos = this.tenantService.currentPointOfService();
        const visit = this.editingEntity();
        if (!pos || !visit) return;

        this.loaderService.show();

        const score = this.feedbackScore();
        const comment = this.feedbackComment().trim();

        try {
            await this.dataService.execute<Visit>('POST', 'checkoutVisit', {
                visitId: visit.id,
                posId: pos.id,
                feedback: score !== undefined || comment.length > 0
                    ? { score: score ?? 0, comment: comment || undefined }
                    : undefined
            });
            this.showSuccessAndGoHome();
        } catch (err) {
            console.error('Erreur checkout:', err);
            this.alertModalService?.showToast(
                this.translationService.instant('vues.qr-code.checkout-form.error-checkout'),
                ToastType.Error,
                3000
            );
        } finally {
            this.loaderService.hide();
        }
    }

    private showSuccessAndGoHome(delayMs = 0): void {
        this.alertModalService?.showToast(
            this.translationService.instant('vues.qr-code.checkout-form.success-message'),
            ToastType.Success,
            3000
        );
        if (delayMs > 0) {
            setTimeout(() => this.goHome(), delayMs);
        } else {
            this.goHome();
        }
    }

    goHome(): void {
        const route = `/qrcode/${this.matwinsVariables.clientId}/${this.matwinsVariables.posId}/home`;
        this.navService.navigateTo(route);
    }
}
